/*
 *
 *  *
 *  *  Copyright (C) THL A29 Limited, a Tencent company. All rights reserved.
 *  *  SPDX-License-Identifier: Apache-2.0
 *  *
 *
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import liststyle from '../list.module.scss';
import PageBox from '@components/ui/PageBox';
import { Text, Justify, Table, TableColumn } from 'tea-component';
import SearchInput from '@components/ui/SearchInput';
import { ContractItem } from '@src/models';
import { GetContractList } from '@src/utils/apis';
import { Link, useParams } from 'react-router-dom';
import { formatUnixTime } from '@src/utils/tools';
const { pageable, autotip } = Table.addons;

export default function Chain() {
  const { chainId } = useParams();
  const [value, setValue] = useState('');
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [list, setList] = useState<ContractItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recordCount, setRecordCount] = useState<number>(0);
  const getList = useCallback(() => {
    setIsLoading(true);
    GetContractList({
      ContractName: value,
      ChainId: chainId,
      Limit: pageSize,
      Offset: pageIndex - 1,
      Order: 'desc',
    })
      .then((res) => {
        setIsLoading(false);
        if (res.GroupList) {
          setList(res.GroupList);
          setRecordCount(res.TotalCount);
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [chainId, value, pageIndex, pageSize]);
  useEffect(() => {
    getList();
  }, [chainId, pageIndex, pageSize]);
  const columns = useMemo<TableColumn[]>(
    () => [
      {
        key: 'Name',
        header: '合约名称',
        align: 'left',
        render: ({ Name }) => <Link to={`/${chainId}/contract/${Name}`}>{Name}</Link>,
      },
      {
        key: 'Version',
        header: '当前版本号',
        align: 'left',
        width: 120,
        render: ({ Version }) => Version || '--',
      },
      {
        key: 'TxCount',
        header: '累计交易数',
        align: 'left',
        width: 120,
      },
      {
        key: 'Creator',
        header: '创建用户',
        align: 'left',
        render: ({ Creator }) => Creator || '--',
      },
      {
        key: 'CreateTimestamp',
        header: '创建时间',
        align: 'left',
        width: 180,
        render: ({ CreateTimestamp }) => (CreateTimestamp ? formatUnixTime(CreateTimestamp) : '--'),
      },
      {
        key: 'UpgradeUser',
        header: '最新更新用户',
        align: 'left',
        render: ({ UpgradeUser }) => UpgradeUser || '--',
      },
      {
        key: 'UpgradeTimestamp',
        header: '最新更新时间',
        align: 'left',
        width: 180,
        render: ({ UpgradeTimestamp }) => (UpgradeTimestamp ? formatUnixTime(UpgradeTimestamp) : '--'),
      },
      {
        key: 'Id',
        header: '操作',
        align: 'center',
        width: 100,
        render: ({ Name }) => (
          <div className={liststyle.setting_c}>
            <Link to={`/${chainId}/contract/${Name}`}>
              <Text theme="primary">查看</Text>
            </Link>
          </div>
        ),
      },
    ],
    [],
  );
  return (
    <PageBox title="合约列表">
      <Justify
        right={
          <SearchInput
            placeholder="请输入合约名称搜索"
            onChange={(value) => setValue(value)}
            onSubmit={getList}
            value={value}
          />
        }
      />
      <div className={liststyle.table}>
        <Table
          style={{
            marginTop: 24,
          }}
          className={liststyle.table}
          compact={false}
          records={list}
          recordKey="a"
          bordered={true}
          disableTextOverflow={true}
          columns={columns}
          addons={[
            autotip({
              isLoading,
            }),
            pageable({
              recordCount,
              pageIndex,
              onPagingChange: (query) => {
                if (query?.pageIndex) {
                  setPageIndex(query.pageIndex);
                }
                if (query?.pageSize) {
                  setPageSize(query.pageSize);
                }
              },
            }),
          ]}
        />
      </div>
    </PageBox>
  );
}
